import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import './ScenarioPlayer.css';
import {
  advanceScenario,
  enterScene,
  fetchScenarioRun,
  finishScene,
  leaveScenarioRun,
  sceneAction,
  startAnnouncerSession,
  startScenarioRun,
  startScenarioRunAt,
  voiceStreamUrl,
} from './api';
import FeedbackLink from './FeedbackLink';
import { useFeedbackInvitation } from './FeedbackInvitationContext';
import { drawing, isAnnounced, isPlayed } from './scenarioScreens';
import ScenarioTranscript from './ScenarioTranscript';
import { SCENARIOS_PATH, scenarioRunPath } from './scenes';
import { createVoiceAudio } from './voiceAudio';
import { createVoiceSession } from './voiceSession';
import WayOn from './WayOn';

/** The frame the relay sends down when the character has moved the round. */
const ROUND_FRAME = 'autolang.round';

// How far along its scenes a run is, as the run itself reports it: in a scene, out of one and not
// yet into what follows, or past the last of them.
const PLAYING = 'PLAYING';
const FINISHED = 'FINISHED';
const OVER = 'OVER';

// What the player is showing: one of the scene's three faces, or the gap between scenes, which is
// the player's own.
const BRIEFING = 'briefing';
const STAGE = 'stage';
const RESULT = 'result';
const INTERSTITIAL = 'interstitial';

/**
 * Which face is up. The run says where it is between its scenes; within the one it is in, the
 * scene says whether it has been begun and whether it has been played out.
 */
function faceOf(play, screen, round, entered) {
  if (play === FINISHED) {
    return INTERSTITIAL;
  }
  if (play === OVER || screen.over(round)) {
    return RESULT;
  }
  return entered || screen.begun(round) ? STAGE : BRIEFING;
}

/**
 * [arriving] as the player should hold it, given the scene [showing] on the screen.
 *
 * Everything the frame says, and what the round on the screen says of the panel: whether a panel
 * is served is the deployment's, and a frame mid-scene is not where that changes.
 */
function withThePanelOf(arriving, showing) {
  const drawn = drawing(arriving);
  const before = drawn && showing[drawn.screen.payload];
  if (!before) {
    return arriving;
  }
  return {
    ...arriving,
    [drawn.screen.payload]: { ...drawn.round, characterStub: before.characterStub },
  };
}

/**
 * Whether what is announced over [runId] can be heard, opening the one session it is said on.
 *
 * The session belongs to the run rather than to any one scene: an announcement is made over a run,
 * and one dialled per scene would open another socket every time the student walks on. So it is
 * opened here, where the run is, and only for a scenario that has anything to say.
 *
 * Nobody asked for it, which is the whole of why every way it can fail ends in the same place.
 * A ticket that cannot be minted, a relay that will not take the socket, an upstream that dies
 * mid-run: the run plays on exactly as it would have, and the only sign is what this returns.
 */
function useAnnouncements(runId, announced) {
  const [heard, setHeard] = useState(true);

  useEffect(() => {
    if (!announced) {
      return undefined;
    }
    setHeard(true);
    const session = createVoiceSession({
      openSocket: async () => {
        const { ticket } = await startAnnouncerSession(runId);
        return new WebSocket(voiceStreamUrl(ticket));
      },
      audio: createVoiceAudio(),
      // Nobody in it to talk to: the microphone is never asked for, so no permission prompt is
      // raised over a session the student never knew about — and nothing greets an empty room.
      microphone: false,
      greeted: false,
      onFailure: () => setHeard(false),
    });
    // The one failure the session cannot report, being the one that leaves it without a socket at
    // all. The speakers it had already opened are let go of here rather than left running.
    session.start().catch(() => {
      session.stop();
      setHeard(false);
    });
    // A relay leg outlives the screen otherwise, and goes on being paid for.
    return () => session.stop();
  }, [announced, runId]);

  return heard;
}

/**
 * The shell a scenario is played in.
 *
 * It starts the run, holds what the run says of itself, shows the board, gives the way out and the
 * way on, and hands frames to whichever scene it is holding. What a scene looks like is the
 * scene's own: this draws the chrome around it and never the place inside.
 */
export default function ScenarioPlayer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Whether the server is inviting this student to say what the product is worth. Relayed rather
  // than decided here, as everywhere else the ask is made.
  const { invited } = useFeedbackInvitation();
  // Which scenario is being played and which run of it. The run every call after the door names
  // is read off the path rather than remembered beside it: the path is what a reload comes back
  // through, so it is the record of which run this is.
  const { scenarioKey, runId } = useParams();
  const [run, setRun] = useState(null);
  const [failed, setFailed] = useState(false);
  // Whether the student has walked in. The run says nothing about it, and nothing should: reading
  // the briefing is a moment of this screen's, not a scene the server has to remember.
  const [entered, setEntered] = useState(false);
  // Whether what was said is being read back over the scenario. Memory-only for the same reason,
  // and the whole of what the overlay is: mounted while it is up, and gone the moment it is not.
  const [reading, setReading] = useState(false);
  // Whether the student has been asked whether they mean to end the run. Memory-only too: the
  // question is a moment of this screen's, and a reload is an answer of no.
  const [asked, setAsked] = useState(false);
  // Whether another run is being opened. A run is paid for, so the way on is shut while one is
  // opening, as it is at the door: the second press of a double press buys nothing.
  const [going, setGoing] = useState(false);
  // A round that arrived while the character was still speaking, waiting on the last word. Read
  // from a handler that fires ten times a second, so a ref rather than state.
  const farewell = useRef(null);
  // Whether what is announced over the run reaches the student. Opened here because the session is
  // the run's; said out loud below because a place that has gone quiet for no stated reason is a
  // student wondering what they missed.
  const heard = useAnnouncements(runId, isAnnounced(scenarioKey));

  // What a run already under way is showing. Reading it is the whole of coming back to one: the
  // scene is the server's and it is written down, so nothing here has to be remembered.
  const resume = useCallback(
    (started) =>
      fetchScenarioRun(started)
        .then(setRun)
        .catch(() => setFailed(true)),
    [],
  );

  // The player is played at a path that names a run, so arriving is always a student coming back
  // to one — bought at the door, or left here by a reload. Nothing is started on the way in.
  useEffect(() => {
    if (isPlayed(scenarioKey)) {
      resume(runId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Whether the scene may be left now that the character has acted. Asked rather than inferred:
  // the frame says what she did, never what the rules make of it. Only the answer to that is taken
  // from it — the round on the screen is the frame's, which arrived first and is newer.
  //
  // A read that fails is reported rather than swallowed. The way out stays shut on an unanswered
  // question, and a student left in front of a control that will never open needs to be told.
  const askWhetherOver = useCallback(
    () =>
      fetchScenarioRun(runId)
        // Everything the run says of itself, and the scene the frame left: it arrived first and
        // is newer. Read in one place, so a run gaining a field is one edit rather than two.
        .then((fresh) => setRun((current) => ({ ...fresh, scene: current.scene })))
        .catch(() => setFailed(true)),
    [runId],
  );

  // The scene a frame carries, in the shape a run reports one: which scene it is, and the round of
  // it. All of that is believed but the panel, which is the deployment's rather than the frame's.
  const applyScene = useCallback((scene) => {
    setRun((current) => ({ ...current, scene: withThePanelOf(scene, current.scene) }));
  }, []);

  // What the character just did, however it reached the player. The frame carries the whole of the
  // student's view, so believing it is the whole of the update — asking the server what it has just
  // said would be a round trip per sentence, and a screen one move behind the character.
  //
  // The round that ends a scene is the one that may have to wait: it takes the place off the screen
  // and the voice with it, and the relay releases it as soon as the answer is generated, seconds
  // before the speakers are through with it. Applied then, it cuts the character off mid-sentence.
  // Nobody is speaking when a developer presses a panel button, so there it lands at once.
  const frameArrived = useCallback(
    (frame, heldForTheGoodbye) => {
      if (frame.type !== ROUND_FRAME) {
        return;
      }
      // Which scene the frame is of is the frame's own to say, and it says it the way a run does.
      // What that scene makes of the round is then its screen's, as it is everywhere else — and it
      // is asked two things, because they are two questions. Whether the round carries an account
      // to end the scene on, which takes the place off the screen and the voice with it. And
      // whether it has met the scene's own condition for being left, which only opens a button. A
      // scenario scored on several scenes has scenes that meet the second and never the first.
      const drawn = drawing(frame.run);
      const ending = drawn && drawn.screen.over(drawn.round);
      if (ending && heldForTheGoodbye) {
        farewell.current = frame.run;
      } else {
        applyScene(frame.run);
      }
      // Asked only when the character moved the one thing the way on turns on, which is the whole
      // reason a screen names it: a run asked after every frame is a round trip per sentence, and
      // one never asked is a traveller shut in a scene they have finished.
      if (drawn && drawn.screen.opensTheWayOut(drawn.round)) {
        askWhetherOver();
      }
    },
    [applyScene, askWhetherOver],
  );

  // The room has gone quiet, or the session has ended and nothing more will be said in it. Either
  // way, what was waiting on the last word has waited long enough.
  const stoppedSpeaking = useCallback(
    (speaking) => {
      if (!speaking && farewell.current) {
        const held = farewell.current;
        farewell.current = null;
        applyScene(held);
      }
    },
    [applyScene],
  );

  // Leaving the scene is the student's own doing, so it goes through their own door rather than
  // the character's: the character's is not served outside development. It ends the scene and not
  // the run — what follows is the interstitial, and the run walks on from there.
  const leaveTheScene = () =>
    finishScene(runId)
      .then(setRun)
      .catch(() => setFailed(true));

  // Walking out of the run itself, which is a different gesture and a different door: the run ends
  // here, wherever here is, and is scored on the journey as it stands. Asked for first, because
  // this is the one press in the player that cannot be taken back.
  const walkOut = () => {
    setAsked(false);
    return leaveScenarioRun(runId)
      .then(setRun)
      .catch(() => setFailed(true));
  };

  // What the student themselves did in the scene they are standing in, named as that scene's own.
  // The server answers with the whole run, which is believed as read — it is the same answer
  // reading the run gives, and it is the server's own word on whether the scene may now be left.
  const act = (action) =>
    sceneAction(runId, action)
      .then(setRun)
      .catch(() => setFailed(true));

  // Pressing on out of the interstitial, into whatever the run has left to walk. Whatever that is
  // is entered from its own door, so the student reads what is coming before they are in it.
  // With one scene there is nothing left, and this is what ends the run.
  const goOn = () => {
    setEntered(false);
    return advanceScenario(runId)
      .then(setRun)
      .catch(() => setFailed(true));
  };

  // Playing again is this run again: another errand is another run, and it is bought at the door
  // this one came in at, which the run itself says. So a student who came in at a scene opens
  // another standing there and rehearses it, rather than walking the errand to reach it again.
  //
  // The finished run is replaced in the history, as the door replaces itself: going back leaves
  // the scenario instead of walking through every run ended on the way.
  const startAnother = () => {
    setFailed(false);
    setEntered(false);
    setGoing(true);
    const openedAt = run.openedAtScene;
    return (openedAt ? startScenarioRunAt(scenarioKey, openedAt) : startScenarioRun(scenarioKey))
      .then((started) => {
        navigate(scenarioRunPath(scenarioKey, started.runId), { replace: true });
        return resume(started.runId);
      })
      .catch(() => setFailed(true))
      .finally(() => setGoing(false));
  };

  const toTheScenarios = () => navigate(SCENARIOS_PATH);

  // Which place is on the screen, the round it draws, and which of its faces is up. Read off the
  // scene the run reports rather than off the path, and read again every time the run moves: a
  // scenario is a sequence of scenes and draws a different place in each of them. Read up here
  // because walking in is something the place is told about before it is drawn — a run that has
  // not been read yet is standing nowhere, and a place this build draws none of is said out loud
  // below.
  const drawn = run ? drawing(run.scene) : null;
  const phase = drawn ? faceOf(run.play, drawn.screen, drawn.round, entered) : null;
  // The scene the student is standing in rather than reading about: the briefing is the door, and
  // the stage is the place itself.
  const walkedInto = phase === STAGE ? run.scene.key : null;

  // Which is told to the place, because some of them do something about somebody being there: a
  // station calls a platform change to whoever is standing in front of the board, never to
  // whoever is still reading what is ahead of them. Told again whenever the screen comes back up —
  // a reload, a second tab — and what that is worth is the scene's own to decide.
  //
  // Nobody asked for it, so a refusal ends where the announcements themselves end: what it costs
  // is the sound, and the place is still playable in silence.
  useEffect(() => {
    if (walkedInto) {
      enterScene(runId).catch(() => {});
    }
  }, [walkedInto, runId]);

  const message = (heading, explain) => (
    <div className="player">
      <div className="player-message">
        <h1>{heading}</h1>
        <p>{explain}</p>
        <button type="button" className="player-primary" onClick={toTheScenarios}>
          {t('player.scenarios')}
        </button>
      </div>
    </div>
  );

  if (!isPlayed(scenarioKey)) {
    return message(t('player.unknown.heading'), t('player.unknown.explain'));
  }
  if (failed) {
    return message(t('player.failed.heading'), t('player.failed.explain'));
  }
  if (!run) {
    return null;
  }

  if (!drawn) {
    return message(t('player.unknown.heading'), t('player.unknown.scene'));
  }

  const { screen, round } = drawn;
  const { Briefing, Stage, Result } = screen;
  // The account the run came to, wherever this screen keeps it. A run can end without one: walking
  // out of a scenario scored inside its one scene ends a visit that was never settled, and there
  // is nothing to draw. Read here so the face below is the one place that answers for it.
  const sheet = screen.sheet(run, round);

  return (
    <div className={`player ${screen.palette}`} data-phase={phase}>
      <header className="player-header">
        {/* The way on out of a scene is deliberately not here. It is a press that moves the run
            forward, so it stands in the place it moves the student out of, beside what they are
            carrying — and never in the same band as the button that throws the run away. */}
        <h1>
          {t(screen.title)} <small>{t(screen.subtitle)}</small>
        </h1>
        {/* Only what is missing, and only while it is. Everything on this screen still works, so
            this is a line rather than anything that has to be dismissed before playing on. */}
        {!heard && <p className="player-unheard" role="status">{t('player.unheard')}</p>}
        <span className="player-clock" aria-label={t('player.clock')}>
          {run.clock.stationTime}
        </span>
        {/* Offered wherever the student is, because reading costs them nothing but time they are
            not being charged for. */}
        <button
          type="button"
          className="player-transcript-open"
          onClick={() => setReading(true)}
        >
          {t('player.transcript.open')}
        </button>
        {/* The other end of the header from the way out of the scene, and never the same button:
            that one finishes a scene and the run walks on, this ends the run. Offered for as long
            as there is a run to walk out of, the gap between two scenes included — a scene whose
            own rules never say it may be left is otherwise a place with no way off it. */}
        {run.play !== OVER && (
          <button
            type="button"
            className="player-walk-out"
            onClick={() => setAsked(true)}
          >
            {t('player.walkOut.open')}
          </button>
        )}
      </header>

      {phase === BRIEFING && (
        <main className="player-briefing">
          <Briefing round={round} />
          <button type="button" className="player-primary" onClick={() => setEntered(true)}>
            {t(screen.enter)}
          </button>
        </main>
      )}

      {/* Two ways a scene moves the run, because there are two ways a run moves: a character acts
          and a frame arrives down the socket, or the student presses something and it goes out
          through their own door. A scene says what was done; what that costs, and what a refusal
          means, are the player's, as they are for the way out and the way on.

          The way on is handed down rather than drawn here, because where it stands is the place's
          business and nowhere is it in the same spot: a station counter puts it under the ticket,
          a bakery under the shopping list. Whether it may be pressed is not the place's — that is
          the run's word, passed through untouched. */}
      {phase === STAGE && (
        <Stage
          round={round}
          runId={runId}
          mayFinish={run.mayFinish}
          onFinish={leaveTheScene}
          onFrame={frameArrived}
          onAct={act}
          onSpeaking={stoppedSpeaking}
        />
      )}

      {phase === INTERSTITIAL && (
        <main className="player-interstitial">
          <h1>{t('player.interstitial.heading')}</h1>
          <button type="button" className="player-primary" onClick={goOn}>
            {t('player.goOn')}
          </button>
        </main>
      )}

      {phase === RESULT && (
        <main className="player-result">
          {/* An ending with no account behind it is said out loud rather than drawn as an empty
              sheet, in the only words the shell has for it: the run is over, and it came to no
              score. What that means in the place they were standing is the place's, not this. */}
          {sheet ? (
            <Result round={round} sheet={sheet} />
          ) : (
            <section>
              <h1>{t('player.ended.heading')}</h1>
              <p>{t('player.ended.explain')}</p>
            </section>
          )}
          {/* A scenario scored inside one scene comes to its account while the run is still going:
              the visit is settled, the place is off the screen, and the run has not walked on yet.
              So the way on stands here too, on the one face that replaces the place it belongs in.
              A scenario scored at the end of the walk never reaches this face before it is over. */}
          {run.play === PLAYING && (
            <WayOn mayFinish={run.mayFinish} onFinish={leaveTheScene} />
          )}
          {run.play === OVER && (
            <div className="player-done">
              <button
                type="button"
                className="player-primary"
                disabled={going}
                onClick={startAnother}
              >
                {t('player.again')}
              </button>
              <button type="button" className="player-secondary" onClick={toTheScenarios}>
                {t('player.scenarios')}
              </button>
              {/* The last screen of a run, which is where a student has just been through the
                  thing they would be asked about. Whether anybody is asked is the server's. */}
              {invited && <FeedbackLink />}
            </div>
          )}
        </main>
      )}
      {/* The transcript of what was said hangs here, above whichever face is up. Whatever is
          underneath stays mounted: the run goes on being the run, and a scene keeps its session
          and its memory of the visit. */}
      {reading && (
        <ScenarioTranscript runId={runId} onClose={() => setReading(false)} />
      )}
      {/* Asked once, because a run walked out of is scored and closed and no second press takes
          that back. The clock is deliberately left running: deciding whether to go is part of the
          run, and a train that arrives while the student thinks about it is a train they caught. */}
      {asked && (
        <div
          className="player-walk-out-ask"
          role="dialog"
          aria-labelledby="player-walk-out-heading"
        >
          <h2 id="player-walk-out-heading">{t('player.walkOut.heading')}</h2>
          <p>{t('player.walkOut.explain')}</p>
          <div className="player-walk-out-answers">
            <button type="button" className="player-primary" onClick={walkOut}>
              {t('player.walkOut.confirm')}
            </button>
            <button
              type="button"
              className="player-secondary"
              onClick={() => setAsked(false)}
            >
              {t('player.walkOut.stay')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
